import classNames from 'classnames';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { ActionPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import { Classes } from 'jss';
import { DayInMonthPlacement, DayInWeekPlacement } from '@tcl-argon-utils/utils';
import { CalendarCellBackdropStyle, CalendarCellHoverState, DateRangePlacement } from './types';

const BORDER_WIDTH = 2;
const TOTAL_HEIGHT = 32;

const useStyles: (data?: {
  theme?: Jss.Theme;
}) => Classes<
  | 'root'
  | 'hoverable'
  | 'hidden'
  | 'inRange'
  | 'selected'
  | 'isOutsideCurrentMonth'
  | 'isToday'
  | 'isBeginningOfWeek'
  | 'isEndOfWeek'
  | 'endOfSelectionLeftBackdrop'
  | 'endOfSelectionRightBackdrop'
  | 'startDateHovered'
  | 'endDateHovered'
  | 'betweenDateHovered'
  | 'buttonContainer'
  | 'noDateHovered'
  | 'singleDateHovered'
  | 'isSingleDateInWeek'
  | 'inRangeBackdrop'
  | 'beginningOfWeekBackdrop'
  | 'endOfWeekBackdrop'
  | 'singleDateInWeekBackdrop'
  | 'disabled'
> = createUseStyles({
  buttonContainer: {
    height: `${32}px`,
    width: `${32}px`,
    paddingLeft: `${BORDER_WIDTH}px`,
    paddingRight: `${BORDER_WIDTH}px`,
    borderTop: `${BORDER_WIDTH}px solid transparent`,
    borderBottom: `${BORDER_WIDTH}px solid transparent`,
    position: 'relative',
    boxSizing: 'border-box',
  },
  startDateHovered: {
    borderTop: `${1}px dashed ${GrayscalePalette[500]}`,
    borderLeft: `${1}px dashed ${GrayscalePalette[500]}`,
    borderBottom: `${1}px dashed ${GrayscalePalette[500]}`,
    paddingTop: '1px',
    paddingBottom: '1px',
    paddingLeft: '1px',
    borderTopLeftRadius: '50px',
    borderBottomLeftRadius: '50px',
  },
  endDateHovered: {
    borderTop: `${1}px dashed ${GrayscalePalette[500]}`,
    borderRight: `${1}px dashed ${GrayscalePalette[500]}`,
    borderBottom: `${1}px dashed ${GrayscalePalette[500]}`,
    paddingTop: '1px',
    paddingBottom: '1px',
    paddingRight: '1px',
    borderTopRightRadius: '50px',
    borderBottomRightRadius: '50px',
  },
  betweenDateHovered: {
    borderTop: `${1}px dashed ${GrayscalePalette[500]}`,
    borderBottom: `${1}px dashed ${GrayscalePalette[500]}`,
    paddingTop: '1px',
    paddingBottom: '1px',
  },
  noDateHovered: {
    paddingLeft: `${BORDER_WIDTH}px`,
    paddingRight: `${BORDER_WIDTH}px`,
    borderTop: `${BORDER_WIDTH}px solid transparent`,
    borderBottom: `${BORDER_WIDTH}px solid transparent`,
  },
  singleDateHovered: {
    borderTop: `${1}px dashed ${GrayscalePalette[500]}`,
    borderRight: `${1}px dashed ${GrayscalePalette[500]}`,
    borderBottom: `${1}px dashed ${GrayscalePalette[500]}`,
    borderLeft: `${1}px dashed ${GrayscalePalette[500]}`,
    padding: '1px',
    borderRadius: '100px',
  },
  root: {
    fontFamily: 'inherit',
    fontSize: '12px',
    lineHeight: '14px',
    fontWeight: 700,
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexBasis: `${100 / 7}%`,
    height: `${TOTAL_HEIGHT}px`,
    width: '32px',
    justifyContent: 'center',
    padding: 0,
    position: 'relative',
    top: `-${BORDER_WIDTH}px`,
    left: `-${BORDER_WIDTH}px`,
    color: ActionPalette[600],
    '&:focus': {
      // Safe to disable because we provide other visual indicators to compensate
      outline: 'none',
    },
  },
  hoverable: {
    '&:hover': {
      borderRadius: '100px',
      backgroundColor: GrayscalePalette[300],
    },
  },
  inRange: {
    backgroundColor: GrayscalePalette[500],
    color: 'white',
    '&:hover': {
      borderRadius: '100px',
      backgroundColor: GrayscalePalette[300],
      color: ActionPalette[600],
    },
  },
  selected: {
    borderRadius: '100px',
    backgroundColor: ActionPalette[600],
    color: 'white',
    '&:hover': {
      borderRadius: '100px',
      backgroundColor: GrayscalePalette[300],
      color: ActionPalette[600],
    },
  },
  isOutsideCurrentMonth: {
    color: GrayscalePalette[500],
  },
  hidden: {
    color: 'white',
    backgroundColor: 'white',
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'white',
    },
  },
  isToday: {
    border: `2px solid ${GrayscalePalette[500]}`,
    borderRadius: '100px',
  },
  isBeginningOfWeek: {
    borderRadius: '100px 0px 0px 100px',
  },
  isEndOfWeek: {
    borderRadius: '0px 100px 100px 0px',
  },
  isSingleDateInWeek: {
    borderRadius: '100px',
  },
  endOfSelectionLeftBackdrop: {
    background: `linear-gradient(90deg, ${GrayscalePalette[500]} 50%, ${GrayscalePalette[100]} 50%)`,
  },
  endOfSelectionRightBackdrop: {
    background: `linear-gradient(90deg, ${GrayscalePalette[100]} 50%, ${GrayscalePalette[500]} 50%)`,
  },
  inRangeBackdrop: {
    background: GrayscalePalette[500],
  },
  beginningOfWeekBackdrop: {
    borderRadius: '100px 0px 0px 100px',
    background: `linear-gradient(90deg, ${GrayscalePalette[500]} 50%, ${GrayscalePalette[500]} 50%)`,
  },
  endOfWeekBackdrop: {
    borderRadius: '0px 100px 100px 0px',
    background: `linear-gradient(90deg, ${GrayscalePalette[500]} 50%, ${GrayscalePalette[500]} 50%)`,
  },
  singleDateInWeekBackdrop: {
    borderRadius: '100px',
    background: GrayscalePalette[100],
  },
  disabled: {
    pointerEvents: 'none',
    color: GrayscalePalette[400],
    background: GrayscalePalette[100],
  },
});

export interface CalendarCellProps {
  day: number;
  hide: boolean;
  isOutlined: boolean;
  isOutsideCurrentMonth: boolean;
  isSelected: boolean;
  dateRangePlacement: DateRangePlacement;
  dayInWeekPlacement?: DayInWeekPlacement;
  dayInMonthPlacement?: DayInMonthPlacement;
  calendarCellHoverStyle?: CalendarCellHoverState;
  calendarCellBackdropStyle?: CalendarCellBackdropStyle;
  isToday?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  disabled?: boolean;
}

export const CalendarCell: React.FC<CalendarCellProps> = ({
  isSelected,
  hide,
  day,
  onClick: handleClick,
  onMouseEnter: handleMouseEnter = () => {},
  onMouseLeave: handleMouseLeave = () => {},
  isOutsideCurrentMonth,
  isToday = false,
  dateRangePlacement,
  dayInWeekPlacement = DayInWeekPlacement.OUTSIDE_OF_WEEK,
  dayInMonthPlacement = DayInMonthPlacement.OUTSIDE_OF_MONTH,
  calendarCellHoverStyle = CalendarCellHoverState.NO_HOVER_STATE,
  calendarCellBackdropStyle = CalendarCellBackdropStyle.NO_BACKDROP,
  disabled = false,
}) => {
  const classes = useStyles();

  return (
    <div
      className={classNames([classes.buttonContainer], {
        [classes.endOfSelectionLeftBackdrop]: calendarCellBackdropStyle === CalendarCellBackdropStyle.LEFT_BACKDROP,
        [classes.endOfSelectionRightBackdrop]: calendarCellBackdropStyle === CalendarCellBackdropStyle.RIGHT_BACKDROP,
        [classes.startDateHovered]: calendarCellHoverStyle === CalendarCellHoverState.START_DATE,
        [classes.betweenDateHovered]: calendarCellHoverStyle === CalendarCellHoverState.MIDDLE_DATE,
        [classes.endDateHovered]: calendarCellHoverStyle === CalendarCellHoverState.END_DATE,
        [classes.noDateHovered]: calendarCellHoverStyle === CalendarCellHoverState.NO_HOVER_STATE,
        [classes.singleDateHovered]: calendarCellHoverStyle === CalendarCellHoverState.SINGLE_DATE,
        [classes.inRangeBackdrop]: dateRangePlacement === DateRangePlacement.IN_RANGE,
        [classes.beginningOfWeekBackdrop]:
          (dayInWeekPlacement === DayInWeekPlacement.BEGINNING_OF_WEEK ||
            dayInMonthPlacement === DayInMonthPlacement.BEGINNING_OF_MONTH) &&
          dateRangePlacement === DateRangePlacement.IN_RANGE &&
          !isSelected,
        [classes.endOfWeekBackdrop]:
          (dayInWeekPlacement === DayInWeekPlacement.END_OF_WEEK ||
            dayInMonthPlacement === DayInMonthPlacement.END_OF_MONTH) &&
          dayInMonthPlacement !== DayInMonthPlacement.BEGINNING_OF_MONTH &&
          dateRangePlacement === DateRangePlacement.IN_RANGE &&
          !isSelected,
        [classes.singleDateInWeekBackdrop]:
          (dayInWeekPlacement === DayInWeekPlacement.BEGINNING_OF_WEEK &&
            dayInMonthPlacement === DayInMonthPlacement.END_OF_MONTH) ||
          (dayInWeekPlacement === DayInWeekPlacement.END_OF_WEEK &&
            dayInMonthPlacement === DayInMonthPlacement.BEGINNING_OF_MONTH),
      })}
    >
      <button
        // `CalendarCell`s should not be tab-able because navigating them is controlled by the parent `div`
        // See `Calendar` for the declaration of that handler.
        tabIndex={-1}
        type="button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={classNames(classes.root, {
          [classes.disabled]: disabled,
          [classes.hidden]: hide,
          [classes.inRange]: dateRangePlacement === DateRangePlacement.IN_RANGE && !hide,
          [classes.isOutsideCurrentMonth]: isOutsideCurrentMonth && !isSelected,
          [classes.selected]: isSelected,
          [classes.isToday]: isToday && dateRangePlacement === DateRangePlacement.NOT_IN_RANGE && !isSelected,
          [classes.hoverable]: dateRangePlacement === DateRangePlacement.NOT_IN_RANGE,
          [classes.isBeginningOfWeek]:
            (dayInWeekPlacement === DayInWeekPlacement.BEGINNING_OF_WEEK ||
              dayInMonthPlacement === DayInMonthPlacement.BEGINNING_OF_MONTH) &&
            dateRangePlacement === DateRangePlacement.IN_RANGE &&
            !isSelected,
          [classes.isEndOfWeek]:
            (dayInWeekPlacement === DayInWeekPlacement.END_OF_WEEK ||
              dayInMonthPlacement === DayInMonthPlacement.END_OF_MONTH) &&
            dayInMonthPlacement !== DayInMonthPlacement.BEGINNING_OF_MONTH &&
            dateRangePlacement === DateRangePlacement.IN_RANGE &&
            !isSelected,
          [classes.isSingleDateInWeek]:
            (dayInWeekPlacement === DayInWeekPlacement.BEGINNING_OF_WEEK &&
              dayInMonthPlacement === DayInMonthPlacement.END_OF_MONTH) ||
            (dayInWeekPlacement === DayInWeekPlacement.END_OF_WEEK &&
              dayInMonthPlacement === DayInMonthPlacement.BEGINNING_OF_MONTH),
        })}
        onClick={handleClick}
        disabled={hide || disabled}
      >
        {day}
      </button>
    </div>
  );
};
